'use client';

import React, { useCallback, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { FollowButton } from '@/components/FollowButton';
import { IdentityRow, identityFromUserSearchResult } from '@/components/identity';
import { getRowExitStyles, useDismissibleRows } from '@/hooks/useDismissibleRows';
import { useUserSearch } from '@/hooks/useUserSearch';
import type { DisplayUser } from '@/lib/api';
import { followsMap } from '@/lib/follows';

/** Defines the authenticated inputs required for inline people search. */
interface HomeAddFriendsPeopleSearchProps {
  userInfo: DisplayUser;
  reloadUserInfo: () => void;
}

/**
 * Renders an inline, debounced people search for the homepage add-friends card.
 *
 * The component uses the existing `/user/search` contract and keeps follow
 * actions inline so users can add connections without leaving the homepage.
 */
const HomeAddFriendsPeopleSearch: React.FC<HomeAddFriendsPeopleSearchProps> = ({
  userInfo,
  reloadUserInfo,
}) => {
  const [query, setQuery] = useState('');

  /** Refreshes user info after a search result row finishes dismissing. */
  const handleSearchResultDismissComplete = useCallback(() => {
    reloadUserInfo();
  }, [reloadUserInfo]);

  const {
    dismissedUsernames: dismissedResultUsernames,
    dismissRow,
    isRowExiting,
  } = useDismissibleRows({ onDismissComplete: handleSearchResultDismissComplete });
  const { results, isLoading, error, hasSearched } = useUserSearch({
    query,
    excludeUsername: userInfo.username,
  });

  const followedUsernames = useMemo(
    () => followsMap(userInfo.followed_users),
    [userInfo.followed_users]
  );

  const visibleResults = useMemo(
    () => results.filter((result) => !dismissedResultUsernames.has(result.username)),
    [dismissedResultUsernames, results]
  );

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Search people"
        placeholder="Search by username"
        size="small"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} aria-label="Searching people" />
          <Typography variant="body2" color="text.secondary">
            Searching people…
          </Typography>
        </Box>
      ) : null}

      {error ? <Alert severity="error">Error searching people.</Alert> : null}

      {!isLoading && !error && visibleResults.length > 0 ? (
        <List disablePadding aria-label="People search results">
          {visibleResults.map((result, index) => {
            const currentlyFollowing = followedUsernames.has(result.username);

            return (
              <ListItem
                key={result.username}
                disableGutters
                divider={index < visibleResults.length - 1}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  ...getRowExitStyles(isRowExiting(result.username)),
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <IdentityRow
                    identity={identityFromUserSearchResult(result)}
                    linkMode="link"
                    avatarSize={32}
                    showFullname={true}
                  />
                </Box>
                <FollowButton
                  authUsername={userInfo.username}
                  followUsername={result.username}
                  currentlyFollowing={currentlyFollowing}
                  noTopicPrompt={true}
                  topics={[]}
                  onDirectFollowFeedbackComplete={() => dismissRow(result.username)}
                />
              </ListItem>
            );
          })}
        </List>
      ) : null}

      {!isLoading && !error && hasSearched && visibleResults.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No people found.
        </Typography>
      ) : null}
    </Stack>
  );
};

export default HomeAddFriendsPeopleSearch;
